import { cloneDeep } from "lodash";
import {
  DynamicPropertyType,
  FieldName,
  FieldSchema,
  FieldValue,
  prepareDynamicPropertyValue,
  Schema,
} from "../Schema";
import { BasicState, CustomState } from "../common/DynamicManager";
import { StaticManager } from "../common/StaticManager";
import {
  DataEnumSchema,
  DataTypes,
  DataVariants,
  EnumOption,
} from "../data/DataManager";

/**
 * Neutral, framework-agnostic filter categories the grid understands. A consumer
 * (e.g. an AG Grid app) maps these to its own filter components/params; the schema
 * itself never references AG Grid. `NONE` disables filtering for the column.
 */
export enum GridFilterTypes {
  SET = "set",
  TEXT = "text",
  NUMBER = "number",
  DATE = "date",
  NONE = "none",
}

/**
 * Named slots for consumer-provided cell renderers. The schema carries only the
 * slot name (a string); the app maps each slot to an actual (React) component.
 * This keeps framework components out of the framework-agnostic library.
 */
export enum GridRenderSlots {
  DATE = "date",
  BOOLEAN = "boolean",
  LINKED_INSTANCE = "linkedInstance",
  REVIEWERS = "reviewers",
  TAGS = "tags",
  STATUS = "status",
  HAZARDOUS = "hazardous",
  ASSISTED = "assisted",
  CONVERSION = "conversion",
}

/**
 * Per-field grid aspect. Every property is optional: sensible values are derived
 * from the other aspects (`data`, `display`, `form`) so a field only needs a
 * `grid` block when its table behaviour differs from those defaults.
 */
export interface GridSchema {
  /**
   * Whether the field is exposed as a grid column at all. Opt-in: defaults to
   * `false`, so only fields that explicitly set `grid.displayed: true` are returned
   * by {@link AgGridManager.getColumns}. This is independent of the `display` aspect
   * (which governs the read-only detail/audit view), letting a field appear in the
   * grid while staying hidden in view details, or vice versa.
   */
  displayed?: boolean;
  /** Whether the column is shown by default vs hidden-but-available (default: false). */
  defaultVisible?: boolean;
  /** Explicit column order; lower comes first. Unordered columns sort after ordered ones. */
  order?: number;
  /** Column header override (default: display.label ?? form.label ?? name). */
  label?: string;
  /** Filter category override (default: derived from data.type / data.variant). */
  filter?: GridFilterTypes;
  /** Filter option keys (e.g. ["contains", "blank"]) for text/number filters. */
  filterOptions?: string[];
  /** Add a "blank" option to a text/number filter. */
  filterBlank?: boolean;
  /** Whether the column is sortable (default: true). */
  sortable?: boolean;
  /** Fixed column width. */
  width?: number;
  /** Pin the column to a side. */
  pinned?: "left" | "right";
  /** Whether the column shows a floating filter. */
  floatingFilter?: boolean;
  /** Exclude the field from the quick-search field set. */
  ignoredForSearch?: boolean;
  /** Restrict a number filter to integer input. */
  integerOnly?: boolean;
  /** Named consumer renderer slot. */
  render?: GridRenderSlots;
  /**
   * Explicit set-filter options, for a SET column whose values are not an enum on the
   * `data` aspect.
   *
   * Several columns are filtered as a set but are not `DataVariants.ENUM` — the form drives
   * them through a picker or a free-text input, so declaring options on `data` would change
   * `DataManager` coercion and `FormManager` rendering for the sake of a grid filter. This
   * keeps the options grid-scoped.
   *
   * Declared as a plain array: the grid aspect is consumed as `Dynamic<GridSchema>`
   * ({@link Schema}), which already admits the value, `() => value` and `{ getter }` forms for
   * every property. A `{ getter }` may be async and is resolved once by
   * {@link AgGridManager.init} — but it must not depend on field values, because the grid
   * resolves it per manager, not per row.
   */
  filterValues?: string[];
  /** Key into the app-supplied dynamic set-filter values (default: field name). */
  dynamicValuesKey?: string;
  /** Sibling field holding the imperial variant (metric/imperial dimension fields). */
  imperialKey?: string;
  /**
   * Column is always visible and cannot be toggled off in the columns tool panel. For a
   * column the grid is meaningless without — the model a row is identified by.
   */
  permanent?: boolean;
}

/**
 * The framework-agnostic per-column output produced by {@link AgGridManager}. It holds
 * no React components and no AG-Grid-specific closures — a thin app-side adapter turns
 * this into the consumer's column definitions (attaching renderers by `renderSlot` and
 * expanding `filter`/`filterOptions` into concrete filter params).
 */
export interface GridColumnDescriptor {
  key: string;
  headerName: string;
  filter: GridFilterTypes;
  filterOptions?: string[];
  filterBlank?: boolean;
  /** Which app-supplied dynamic-values entry feeds a SET filter (undefined ⇒ use enumValues). */
  dynamicValuesKey?: string;
  /** Statically-resolved enum option labels, when the schema defines them. */
  enumValues?: string[];
  sortable: boolean;
  width?: number;
  pinned?: "left" | "right";
  floatingFilter?: boolean;
  hide: boolean;
  order: number;
  displayed: boolean;
  renderSlot?: GridRenderSlots;
  integerOnly?: boolean;
  ignoredForSearch?: boolean;
  imperialKey?: string;
  permanent?: boolean;
}

/**
 * A single entry of the app-facing `columnData` array consumed by the equipment grid.
 *
 * This is the legacy wire shape the app already persists in user profiles, so the field
 * names are fixed by that contract rather than chosen here. Notably it carries `show`
 * rather than `hide`: a truthy `hide` is copied straight into the AG Grid colDef and,
 * because the column definitions are rebuilt on every render, the grid would re-apply it
 * and discard whatever the user picked in the columns tool panel. Visibility is owned by
 * the app's column-apply step instead.
 */
export interface GridColumnData {
  key: string;
  /** Column header. */
  val: string;
  order: number;
  /** Visible by default. Inverse of {@link GridColumnDescriptor.hide}. */
  show: boolean;
  sortStatus: boolean;
  /** Sibling field holding the imperial value, for metric/imperial columns. */
  imperialField?: string;
  /**
   * Statically-known values for a SET filter. Absent when the schema cannot resolve
   * them — the app then supplies them at runtime keyed by {@link dynamicValuesKey}.
   */
  filterValues?: string[];
  /**
   * Which app-supplied values entry feeds this SET filter when {@link filterValues} is
   * absent. Defaults to the field name, so it lets a column point its fallback at a
   * differently-named entry instead of relying on the two happening to match.
   */
  dynamicValuesKey?: string;
  /**
   * Neutral filter category. The app maps this to its own filter component — a SET becomes
   * AG Grid's `agSetColumnFilter`, and so on — rather than the schema naming one.
   */
  filter: GridFilterTypes;
  /** Offer a "blank" option alongside the filter's other conditions. */
  filterBlank: boolean;
  /** Restrict a number filter to whole numbers. */
  integerOnly: boolean;
  /** Exclude from the quick-search field set. */
  ignoredForSearch: boolean;
  /**
   * Named renderer slot the app resolves to a component. Absent when the column renders as
   * plain text.
   */
  renderSlot?: GridRenderSlots;
}

/** Order assigned to columns without an explicit `grid.order`, so they sort last. */
const UNORDERED = Number.MAX_SAFE_INTEGER;

export interface GridFieldState extends BasicState, GridColumnDescriptor {
  name: FieldName;
}

export interface GridState extends CustomState {
  columns: GridColumnDescriptor[];
}

export const defaultGridState: GridState = {
  loading: false,
  columns: [],
};

/**
 * Read-only manager that turns a {@link Schema} into an ordered list of neutral grid
 * column descriptors. Extends {@link StaticManager} — all column metadata is derived
 * once at construction time from field-level schema, with no per-row value logic.
 *
 * The one exception is {@link GridSchema.filterValues} declared as a getter, which may read
 * master data and is therefore async. {@link init} resolves those; a manager built from a
 * schema that declares none needs no `init` at all.
 */
export class AgGridManager extends StaticManager<GridFieldState, GridState> {
  customState: GridState = defaultGridState;

  /** Resolves once; retained so repeat {@link init} calls neither refetch nor race. */
  private initialised?: Promise<this>;

  constructor(schema: Schema = {}) {
    super(schema);
    this.updateCustomState();
  }

  /**
   * Resolve every `grid.filterValues` getter and fold the results into the column state.
   *
   * Only needed for a schema that declares getters — a plain array is already resolved by
   * `getMapping`, so existing callers that never `init` keep working unchanged.
   *
   * Idempotent: the first call's promise is retained and returned to every later caller, so
   * the getters run exactly once per manager however many times this is called.
   *
   * @returns This manager, once its filter values are resolved.
   */
  public async init(): Promise<this> {
    this.initialised = this.initialised ?? this.resolveFilterValues();
    return this.initialised;
  }

  /** Awaits every declared getter concurrently, then rebuilds the column list once. */
  private async resolveFilterValues(): Promise<this> {
    const pending = Object.keys(this.schema)
      .map((fieldName) => ({
        fieldName,
        filterValues: (this.schema[fieldName]?.grid as GridSchema | undefined)?.filterValues,
      }))
      // A plain array is already in the state; only a getter is left to await.
      .filter(({ filterValues }) => filterValues !== undefined && !Array.isArray(filterValues));

    if (!pending.length) {
      return this;
    }

    await Promise.all(
      pending.map(async ({ fieldName, filterValues }) => {
        const prepared = prepareDynamicPropertyValue<string[]>(
          filterValues as DynamicPropertyType<string[]>
        );
        // No field values are passed: a grid filter's options describe the column, not a row.
        const resolved = await prepared.getter({});
        if (this.state[fieldName]) {
          this.state[fieldName].enumValues = resolved ?? [];
        }
      })
    );

    this.updateCustomState();
    return this;
  }

  /**
   * True when the field is a boolean-style enum (options whose values are booleans),
   * which the grid renders as a Yes/No set filter.
   */
  private isBooleanEnum(field: FieldSchema): boolean {
    if (field.data?.type === DataTypes.BOOLEAN) {
      return true;
    }
    if (field.data?.variant !== DataVariants.ENUM) {
      return false;
    }
    const options = (field.data as DataEnumSchema)?.options;
    return (
      Array.isArray(options) &&
      options.length > 0 &&
      options.every(
        (option) =>
          typeof option !== "string" && typeof option.value === "boolean"
      )
    );
  }

  /** Derive the filter category from other aspects when `grid.filter` is absent. */
  private deriveFilterType(field: FieldSchema): GridFilterTypes {
    if (field.data?.type === DataTypes.DATE) {
      return GridFilterTypes.DATE;
    }
    if (this.isBooleanEnum(field)) {
      return GridFilterTypes.SET;
    }
    if (field.data?.variant === DataVariants.ENUM) {
      return GridFilterTypes.SET;
    }
    if (field.data?.type === DataTypes.NUMBER) {
      return GridFilterTypes.NUMBER;
    }
    return GridFilterTypes.TEXT;
  }

  private resolveHeaderName(field: FieldSchema, grid: GridSchema): string {
    return (
      grid.label ??
      (field.display?.label as string | undefined) ??
      (field.form?.label as string | undefined) ??
      field.name
    );
  }

  /**
   * Resolve enum option labels synchronously from the static schema definition.
   * Dynamic options (function-based) resolve to an empty array — the app then
   * supplies values at runtime via the dynamic values key.
   */
  private resolveEnumValues(field: FieldSchema): string[] | undefined {
    if (field.data?.variant !== DataVariants.ENUM) {
      return undefined;
    }
    const rawOptions = (field.data as DataEnumSchema)?.options;
    if (typeof rawOptions === "function" || !Array.isArray(rawOptions)) {
      return [];
    }
    return (rawOptions as EnumOption<FieldValue>[]).map((option) =>
      typeof option === "string" ? option : option.label
    );
  }

  /**
   * Resolve `grid.filterValues` as far as can be done synchronously.
   *
   * A plain array is taken as-is. A getter is left alone and returns `undefined`, so the
   * column keeps whatever `resolveEnumValues` produced until {@link init} awaits it — which
   * is what lets the constructor stay synchronous.
   */
  private resolveStaticFilterValues(grid: GridSchema): string[] | undefined {
    return Array.isArray(grid.filterValues) ? grid.filterValues : undefined;
  }

  /** A boolean set-filter always renders through the BOOLEAN slot unless the field explicitly requests another slot. */
  private resolveRenderSlot(
    field: FieldSchema,
    grid: GridSchema,
    filter: GridFilterTypes
  ): GridRenderSlots | undefined {
    if (grid.render) {
      return grid.render;
    }
    if (filter === GridFilterTypes.SET && this.isBooleanEnum(field)) {
      return GridRenderSlots.BOOLEAN;
    }
    if (filter === GridFilterTypes.DATE) {
      return GridRenderSlots.DATE;
    }
    return undefined;
  }

  getMapping(field: FieldSchema): Omit<GridFieldState, keyof BasicState> {
    const grid = (field.grid ?? {}) as GridSchema;
    const filter = grid.filter ?? this.deriveFilterType(field);

    return {
      name: field.name,
      key: field.name,
      headerName: this.resolveHeaderName(field, grid),
      filter,
      filterOptions: grid.filterOptions,
      filterBlank: grid.filterBlank ?? false,
      sortable: grid.sortable ?? true,
      width: grid.width,
      pinned: grid.pinned,
      floatingFilter: grid.floatingFilter,
      displayed: grid.displayed ?? false,
      order: grid.order ?? UNORDERED,
      hide: !(grid.defaultVisible ?? false),
      renderSlot: this.resolveRenderSlot(field, grid, filter),
      integerOnly: grid.integerOnly ?? false,
      ignoredForSearch: grid.ignoredForSearch ?? false,
      permanent: grid.permanent ?? false,
      dynamicValuesKey:
        grid.dynamicValuesKey ??
        (filter === GridFilterTypes.SET ? field.name : undefined),
      imperialKey: grid.imperialKey,
      enumValues: this.resolveStaticFilterValues(grid) ?? this.resolveEnumValues(field),
    };
  }

  updateCustomState(): void {
    const columns = Object.values(this.state)
      .filter((state) => state.displayed)
      .map((state) => {
        const { name, loading, ...descriptor } = state;
        return descriptor as GridColumnDescriptor;
      })
      .sort((a, b) => a.order - b.order);

    this.customState = {
      loading: false,
      columns,
    };
  }

  /** Ordered, visible-aware column descriptors. */
  public getColumns(): GridColumnDescriptor[] {
    return cloneDeep(this.customState.columns);
  }

  /**
   * Ordered column data in the app-facing {@link GridColumnData} shape.
   *
   * Filter values are included only for SET columns whose options the schema can resolve
   * statically. Options built from loaded master data (tags, SOPs, sites, lab areas)
   * resolve once the schema is prepared with a populated context; cascading options and
   * columns with no schema options at all resolve to nothing here, and the app keeps
   * supplying those at runtime via `dynamicValuesKey`.
   */
  public generateColumnData(): GridColumnData[] {
    return this.getColumns().map((column) => {
      const columnData: GridColumnData = {
        key: column.key,
        val: column.headerName,
        order: column.order,
        show: !column.hide,
        sortStatus: column.sortable,
        filter: column.filter,
        filterBlank: column.filterBlank ?? false,
        integerOnly: column.integerOnly ?? false,
        ignoredForSearch: column.ignoredForSearch ?? false,
      };

      if (column.renderSlot) {
        columnData.renderSlot = column.renderSlot;
      }

      if (column.imperialKey) {
        columnData.imperialField = column.imperialKey;
      }

      if (column.dynamicValuesKey) {
        columnData.dynamicValuesKey = column.dynamicValuesKey;
      }

      if (
        column.filter === GridFilterTypes.SET &&
        column.enumValues &&
        column.enumValues.length > 0
      ) {
        // Deduplicated and sorted: master-data-backed options routinely repeat a label
        // (the same lab area across several sites), and a set filter should list each
        // choice once, in a predictable order.
        columnData.filterValues = [...new Set(column.enumValues)].sort();
      }

      return columnData;
    });
  }

  /**
   * Metric field name → imperial sibling field name, for every dimension column.
   *
   * Exposed separately because the app resolves this on the cell-render path, where it
   * cannot await a prepared schema. The mapping is static grid metadata and never depends
   * on the loaded context, so it is safe to read from a schema built without one.
   */
  public getImperialFieldMap(): Record<string, string> {
    const imperialFields: Record<string, string> = {};

    this.customState.columns.forEach((column) => {
      if (column.imperialKey) {
        imperialFields[column.key] = column.imperialKey;
      }
    });

    return imperialFields;
  }

  public getGridState(): GridState {
    return cloneDeep(this.customState);
  }
}
